import { Text } from '@televet/kibble-ui';
import { Center, Flex, Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@televet/kibble-ui/build/chakra';
import React from 'react';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { TaskColumnHeader } from './TaskColumnHeader';
import { SlideOutList } from '../../../../shared/components/SlideOutList';
import { TaskRow } from './TaskRow';
import { TaskSortCriteria, TaskSortingKeys, TaskWithMemoizedValues } from 'pages/Tasks/types';
import { StepPagination, StepPaginationProps } from 'shared/components/Pagination/StepPagination';

type TaskTableProps = {
  tasks: TaskWithMemoizedValues[];
  onSort: (key: TaskSortingKeys) => void;
  borderLeftColor?: string;
  completedTasksOnly?: boolean;
  onRemoveElement: (id: string) => void;
  currentSort?: TaskSortCriteria;
  paginationProps: StepPaginationProps;
  onStatusChange: () => void;
  taskListId: string;
  onTaskEdit?: (task: TaskWithMemoizedValues) => void;
};

export const TaskTable = ({
  tasks,
  borderLeftColor,
  onSort,
  onRemoveElement,
  onStatusChange,
  completedTasksOnly,
  currentSort,
  paginationProps,
  taskListId,
  onTaskEdit,
}: TaskTableProps): JSX.Element => {
  return (
    <Table>
      <Thead>
        <Tr>
          <TaskColumnHeader
            label="Task"
            currentSort={currentSort}
            onSort={onSort}
            sortKey="name"
            testId={`task-table-${taskListId}-taskName-column-header`}
          />
          {completedTasksOnly ? (
            <>
              <TaskColumnHeader
                label="Client"
                currentSort={currentSort}
                onSort={onSort}
                sortKey="clinicPetParent.firstName"
                testId={`task-table-${taskListId}-clientName-column-header`}
              />
              <TaskColumnHeader label="Completed" currentSort={currentSort} onSort={onSort} sortKey="completedAt" />
              <Th px={4} py={2} borderBottomWidth="1px" borderColor="border.default">
                <Flex alignItems="center">
                  <Text size="xs" color="text.subtle" fontWeight="normal">
                    Completed by
                  </Text>
                </Flex>
              </Th>
            </>
          ) : (
            <>
              <TaskColumnHeader label="Status" currentSort={currentSort} onSort={onSort} sortKey="status" />
              <TaskColumnHeader
                label="Client"
                currentSort={currentSort}
                onSort={onSort}
                sortKey="clinicPetParent.firstName"
                testId={`task-table-${taskListId}-clientName-column-header`}
              />
              <TaskColumnHeader label="Created" currentSort={currentSort} onSort={onSort} sortKey="createdAt" />
              <TaskColumnHeader label="Assignees" />
            </>
          )}
        </Tr>
      </Thead>
      <Tbody>
        {tasks.length === 0 ? (
          <Tr>
            <Td colSpan={5}>
              <Center>
                <Text size="sm" color="text.subtle" pt={2}>
                  No results found
                </Text>
              </Center>
            </Td>
          </Tr>
        ) : (
          <SlideOutList
            initialList={tasks}
            renderItem={({ key, data, elementRef, slideOut, style }): JSX.Element => {
              return (
                <TaskRow
                  key={key}
                  data={data}
                  elementRef={elementRef}
                  slideOut={slideOut}
                  style={style}
                  borderLeftColor={borderLeftColor}
                  completedTasksOnly={completedTasksOnly}
                  onStatusChange={onStatusChange}
                  onTaskEdit={onTaskEdit}
                />
              );
            }}
            onRemoveElement={onRemoveElement}
            renderEmptyList={(): JSX.Element => (
              <Tr>
                <Td colSpan={5}>
                  <Center>
                    <LoadingSpinner direction="row" />
                  </Center>
                </Td>
              </Tr>
            )}
          />
        )}
      </Tbody>
      <Tfoot>
        <Tr>
          <Td colSpan={5} px={4} pt={2} pb={0} border="none">
            <Flex justifyContent="flex-end">
              <StepPagination {...paginationProps} dataTestId={taskListId} />
            </Flex>
          </Td>
        </Tr>
      </Tfoot>
    </Table>
  );
};
